import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { API_URL } from '@/lib/backendUrl';
import scrollUnderHeader from '@/lib/scrollUnderHeader';
import DayCircle, { moreEventsLine } from '@/components/daily3/DayCircle';
import { useNowMinutes } from '@/components/daily3/DayTimeline';

/**
 * «Твой день сегодня» — компактная светлая карточка под расшифровками.
 *
 * Сама не показывается: её зовёт плашка «Твой день сегодня» в колонке
 * программ главной, и расчёт уходит только в момент монтирования, то есть
 * по клику.
 *
 * Состав и формулировки — дневного письма v1.5: сервер считает тот же
 * прогноз (UnifiedForecastService) и отдаёт готовые строки
 * (services/day_card.py), здесь только вёрстка. Секции идут столбиком и
 * рисуются только когда в них что-то есть; спокойный день — одной строкой.
 * Лесенка тарифа на сервере: не-ПРО не получает из фоновых процессов ничего,
 * кроме числа.
 *
 *   гость      — Настроение: 3 строки, ближайшие к текущему часу, остальное —
 *                размытая заглушка (не из данных: их сервер не присылает) и
 *                кнопка регистрации; на круге — 3 открытые точки; Аспекты дня
 *                закрыты, вместо текста
 *                кнопка «Регистрация в 1 клик — открыть аспекты дня» (то же
 *                окно регистрации, place=day_card); у фоновых — только строка
 *                «Идут N процессов», без кнопки;
 *   ЛАЙТ/триал — Аспекты дня открыты полностью; у фоновых счётчик и
 *                «Доступно в ПРО» (→ /pricing);
 *   ПРО        — карточки процессов и «Полный прогноз дня».
 *
 * «Сегодня» вошедшему сервер считает как в письме (пояс из профиля), гостю —
 * по поясу браузера, который уходит в запросе.
 *
 * Сверху — мини-круг дня (components/daily3/DayCircle, compact): та же
 * раскладка, что у круга на /transits/results, из того же прогноза
 * (card.circle), с той же лесенкой; всплывашки — по тапу.
 */

const POLARITY_BAR = {
  harmonic: 'border-emerald-400',
  tense: 'border-rose-400',
  neutral: 'border-sky-400',
};

function viewerTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_e) {
    return null;
  }
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function Section({ title, children, testId }) {
  return (
    <div className="mb-2.5 break-inside-avoid" data-testid={testId}>
      <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-sky-800">{title}</h3>
      {children}
    </div>
  );
}

function Row({ polarity, line, text }) {
  return (
    <div className={`border-l-2 pl-2 ${POLARITY_BAR[polarity] || POLARITY_BAR.neutral}`}>
      <div className="text-[13px] font-semibold leading-snug text-slate-900">{line}</div>
      {text && <div className="text-[12px] leading-snug text-slate-600">{text}</div>}
    </div>
  );
}

// Заглушка закрытых строк Настроения гостя: серые полоски под размытием,
// ширины — по номеру строки, не из данных.
const STUB_WIDTHS = ['w-4/5', 'w-3/5', 'w-11/12', 'w-2/3'];

function LockedMoonRows({ count, onRegister }) {
  const rows = Math.min(count, 3);
  return (
    <div className="mt-1.5" data-testid="day-today-moon-locked">
      <div className="pointer-events-none select-none space-y-1.5 blur-[3px]" aria-hidden>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="border-l-2 border-slate-300 pl-2">
            <div className={`h-3 rounded bg-slate-300 ${STUB_WIDTHS[i % STUB_WIDTHS.length]}`} />
            <div className={`mt-1 h-2.5 rounded bg-slate-200 ${STUB_WIDTHS[(i + 2) % STUB_WIDTHS.length]}`} />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => { if (typeof onRegister === 'function') onRegister(); }}
        className="mt-2 flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-left text-[12px] font-semibold text-white hover:bg-sky-700"
        data-testid="day-today-moon-register"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" />
        {moreEventsLine(count)}
      </button>
    </div>
  );
}

export default function DayTodayCard({ birthParams, isAuthenticated, userKey, onRegister, scrollKey = 0 }) {
  const [card, setCard] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | error
  const now = useNowMinutes(card && card.circle);
  const rootRef = useRef(null);
  const scrollAtRef = useRef(0);

  // Подводка кадра по нажатию плашки дня: верх карточки — под шапку. Сразу
  // после отрисовки и ещё раз, когда день досчитался: пока шёл расчёт, выше
  // карточки могло дорасти превью расшифровки, и карточка съезжала вниз.
  useEffect(() => {
    if (!scrollKey) return undefined;
    scrollAtRef.current = Date.now();
    const id = window.requestAnimationFrame(() => scrollUnderHeader(rootRef.current));
    return () => window.cancelAnimationFrame(id);
  }, [scrollKey]);
  useEffect(() => {
    if (state === 'loading' || !scrollAtRef.current) return undefined;
    const fresh = Date.now() - scrollAtRef.current < 8000;
    scrollAtRef.current = 0;
    if (!fresh) return undefined;
    const id = window.requestAnimationFrame(() => scrollUnderHeader(rootRef.current));
    return () => window.cancelAnimationFrame(id);
  }, [state]);

  // Перезапрос — на новую карту и на смену человека: после регистрации в
  // окне карточка перерисовывается уже по его тарифу.
  useEffect(() => {
    if (!birthParams) return undefined;
    let cancelled = false;
    setState('loading');
    const token = (() => {
      try { return localStorage.getItem('auth_token'); } catch (_e) { return null; }
    })();
    const p = birthParams;
    fetch(`${API_URL}/calculator/daily/today-card`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        birth_date: `${p.year}-${pad2(p.month)}-${pad2(p.day)}`,
        birth_time: p.time_unknown ? null : p.time,
        birth_time_unknown: !!p.time_unknown,
        latitude: p.latitude,
        longitude: p.longitude,
        timezone: p.timezone || null,
        viewer_timezone: viewerTimezone(),
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        setCard(d);
        setState(d ? 'ready' : 'error');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => { cancelled = true; };
  }, [birthParams, isAuthenticated, userKey]);

  if (!birthParams) return null;

  const access = card && card.access;
  const hints = (card && card.hints && card.hints.items) || [];
  const aspects = (card && card.aspects) || {};
  const aspectItems = aspects.items || [];
  const moonItems = ((card && card.moon && card.moon.items) || []);
  const moonLocked = (card && card.moon && card.moon.locked_count) || 0;
  const bg = (card && card.background) || {};
  const circle = card && card.circle;

  return (
    <div
      id="day-today-card"
      ref={rootRef}
      style={{ scrollMarginTop: 'calc(4rem + var(--promo-bar-h, 0px) + 8px)' }}
      className="mx-auto mt-3 max-w-5xl rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-2.5 md:px-4"
      data-testid="day-today"
    >
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 className="font-heading text-base font-bold text-slate-900">Твой день сегодня</h2>
        {card && card.date_label && (
          <span className="text-xs text-slate-500" data-testid="day-today-date">{card.date_label}</span>
        )}
      </div>

      {state === 'error' ? (
        <p className="text-[13px] text-slate-600" data-testid="day-today-error">
          Не получилось посчитать день. Попробуй ещё раз чуть позже.
        </p>
      ) : state === 'loading' || !card ? (
        <div className="flex items-center gap-2 py-2 text-[13px] text-slate-700" data-testid="day-today-loading">
          <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
          Считаю твой день…
        </div>
      ) : (
        <div>
          {circle && (
            <div className="mb-3 rounded-xl bg-white px-2 py-3" data-testid="day-today-circle">
              <DayCircle data={circle} now={now} compact
                onRegister={() => { if (typeof onRegister === 'function') onRegister(); }} />
            </div>
          )}
          {hints.length > 0 && (
            <div className="mb-2 space-y-0.5" data-testid="day-today-hints">
              {hints.map((h, i) => (
                <p key={i} className="text-[13px] leading-snug text-slate-800">
                  <span className={`font-bold ${h.kind === 'avoid' ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {h.label}:
                  </span>{' '}
                  {h.text}
                </p>
              ))}
            </div>
          )}

          {/* Секции текут в две колонки по содержимому (CSS columns), а не
              тремя равными: пустые не рисуются, короткие не тянутся. */}
          <div className="md:columns-2 md:gap-5">
          {(moonItems.length > 0 || moonLocked > 0) && (
            <Section title={card.moon.title} testId="day-today-moon">
              <div className="space-y-1">
                {moonItems.map((m, i) => <Row key={i} polarity={m.polarity} line={m.line} text={m.text} />)}
              </div>
              {moonLocked > 0 && <LockedMoonRows count={moonLocked} onRegister={onRegister} />}
            </Section>
          )}

          {aspects.locked ? (
            <Section title={aspects.title} testId="day-today-aspects-locked">
              <button
                type="button"
                onClick={() => { if (typeof onRegister === 'function') onRegister(); }}
                className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-left text-[12px] font-semibold text-white hover:bg-sky-700"
                data-testid="day-today-register"
              >
                <Lock className="h-3.5 w-3.5 shrink-0" />
                Регистрация в 1 клик — открыть аспекты дня
              </button>
            </Section>
          ) : aspectItems.length > 0 ? (
            <Section title={aspects.title} testId="day-today-aspects">
              {aspects.when_label && <div className="mb-0.5 text-[11px] text-slate-500">{aspects.when_label}</div>}
              <div className="space-y-1">
                {aspectItems.map((a, i) => (
                  <Row key={i} polarity={a.polarity}
                    line={a.time ? `${a.time} — ${a.theme}` : a.theme} text={a.advice} />
                ))}
              </div>
              {aspects.more_line && (
                <p className="mt-1 text-[12px] font-semibold text-slate-700" data-testid="day-today-more">{aspects.more_line}</p>
              )}
            </Section>
          ) : (
            <p className="mb-2.5 break-inside-avoid text-[13px] leading-snug text-slate-800" data-testid="day-today-calm">
              {aspects.next_exact
                ? `Сегодня спокойный день, ближайший аспект — ${aspects.next_exact}`
                : 'Сегодня спокойный день.'}
            </p>
          )}

          {bg.count > 0 && (
            <Section title={bg.title} testId="day-today-background">
              {access === 'pro' ? (
                <div className="space-y-1">
                  {(bg.cards || []).map((c, i) => (
                    <div key={i} className={`border-l-2 pl-2 ${POLARITY_BAR[c.polarity] || POLARITY_BAR.neutral}`}>
                      <div className="text-[13px] font-semibold leading-snug text-slate-900">
                        {c.title_line}
                        {c.badge && (
                          <span className="ml-1.5 rounded bg-violet-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase text-violet-700">{c.badge}</span>
                        )}
                        {c.period_line && <span className="ml-1.5 text-[11px] font-normal text-slate-500">{c.period_line}</span>}
                      </div>
                      {(c.header || c.text) && (
                        <div className="text-[12px] leading-snug text-slate-600">
                          {c.header}{c.header && c.text ? ' ' : ''}{c.text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : access === 'guest' ? (
                <p className="text-[13px] font-semibold leading-snug text-violet-900" data-testid="day-today-influence">
                  {bg.guest_line}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <p className="text-[13px] font-semibold leading-snug text-violet-900" data-testid="day-today-influence">
                    <Lock className="mr-1 inline h-3.5 w-3.5 align-[-2px] text-violet-600" />
                    {bg.influence_line}
                  </p>
                  <Link
                    to="/pricing"
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-violet-700"
                    data-testid="day-today-pro-link"
                  >
                    Доступно в ПРО
                  </Link>
                </div>
              )}
            </Section>
          )}

          </div>

          {access === 'pro' && card.full_url && (
            <div>
              <Link
                to={card.full_url}
                className="inline-block rounded-lg bg-sky-600 px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-sky-700"
                data-testid="day-today-full"
              >
                Полный прогноз дня →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
